import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-simple-toasts";
import { firebase, functions, firestore } from "../firebaseConfig";
import {
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { calculateDogTimeRemaining, formatCountdownTimer } from "../utils/HELPER_FUNCTIONS";

const ADMIN_PASSWORD = "pledgefast2025";

const AdminPage = () => {
  const [avPets, setAvPets] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [password, setPassword] = useState("");
  const [requiresCapturePledges, setRequiresCapturePledges] = useState([]);
  const [unprocessedClaims, setUnprocessedClaims] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [expandedPledges, setExpandedPledges] = useState({});

  useEffect(() => {
    const adminAccess = localStorage.getItem('adminAccess');
    if (adminAccess === 'true') {
      setHasAccess(true);
      fetchAllData();
    }
  }, []);

  // Live countdown timer - updates every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    try {
      // Fetch claims
      const claimsSnapshot = await firestore.collection("claims").get();
      const allClaims = [];
      claimsSnapshot.forEach(doc => {
        allClaims.push({ id: doc.id, ...doc.data() });
      });

      const pendingClaims = allClaims.filter(
        (claim) => claim?.claimStatus === "pending"
      );

      // Fetch pledges
      const dogsSnapshot = await firestore.collection("web-available-dogs").get();
      const allPledges = [];
      const claimSlugs = [...new Set(pendingClaims.map(claim => claim.slug))];

      for (const slug of claimSlugs) {
        try {
          const pledgesSnapshot = await firestore.collection(`pledges`).where("slug", "==", slug).get();
          pledgesSnapshot.forEach(pledgeDoc => {
            const pledgeData = { id: pledgeDoc.id, ...pledgeDoc.data() };
            allPledges.push(pledgeData);
          });
        } catch (error) {
          console.error(`Error accessing pledges for ${slug}:`, error);
        }
      }

      const capturePledges = allPledges.filter(
        (pledge) => pledge?.status === "requires_capture"
      );

      setRequiresCapturePledges(capturePledges);

      // Fetch available pets
      const availablePets = {};
      dogsSnapshot.forEach(doc => {
        availablePets[doc.id] = { id: doc.id, ...doc.data() };
      });
      setAvPets(availablePets);

      const matchedPledgesAndClaims = pendingClaims.map((claim) => {
        const matchedPledges = capturePledges.filter(
          (pledge) => pledge.slug === claim.slug
        );
        return { claim, pledges: matchedPledges };
      });

      // Get pet profiles
      const availablePetProfiles = await Promise.all(
        matchedPledgesAndClaims.map(async ({ claim }) => {
          const petDoc = await firestore.collection("web-available-dogs").where("slug", "==", claim.slug).limit(1).get();
          return petDoc.empty ? null : { id: petDoc.docs[0].id, ...petDoc.docs[0].data() };
        })
      );

      // Fetch subscriptions
      const subscriptionsSnapshot = await firestore.collection("subscriptions").get();
      const allSubscriptions = [];
      subscriptionsSnapshot.forEach(doc => {
        const subscriptionData = { id: doc.id, ...doc.data() };
        if (subscriptionData.status === "trialing" || subscriptionData.status === "active") {
          allSubscriptions.push(subscriptionData);
        }
      });

      // Fetch rescue org data for each claim
      const claimsWithRescueOrg = await Promise.all(
        pendingClaims.map(async (claim) => {
          let rescueOrgData = {
            organizationName: "Unknown Organization",
            email: "",
            phoneNumber: "",
            address: "",
            website: ""
          };
          try {
            // Get user who claimed
            const userDoc = await firestore.collection("users").doc(claim.claimedBy).get();
            if (userDoc.exists) {
              const userData = userDoc.data();
              // Get rescue organization
              if (userData.rescueOrganizationId) {
                const rescueOrgDoc = await firestore.collection("rescueOrganizations").doc(userData.rescueOrganizationId).get();
                if (rescueOrgDoc.exists) {
                  const orgData = rescueOrgDoc.data();
                  rescueOrgData = {
                    organizationName: orgData.organizationName || "Unknown Organization",
                    email: orgData.email || "",
                    phoneNumber: orgData.phoneNumber || "",
                    address: orgData.address || "",
                    website: orgData.website || ""
                  };
                }
              }
            }
          } catch (error) {
            console.error("Error fetching rescue org:", error);
          }
          return { ...claim, rescueOrgData };
        })
      );

      // Combine all data
      const combinedData = matchedPledgesAndClaims.map((item, index) => {
        const matchedSubscriptions = allSubscriptions.filter(
          (subscription) => subscription.dogSlug === item.claim.slug
        );
        return {
          ...item,
          claim: claimsWithRescueOrg[index],
          petProfile: availablePetProfiles[index],
          subscriptions: matchedSubscriptions,
        };
      });

      setUnprocessedClaims(combinedData);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast("Failed to fetch data. Please try again.");
    }
  };

  const handleSignIn = async () => {
    try {
      if (password === ADMIN_PASSWORD) {
        localStorage.setItem('adminAccess', 'true');
        setHasAccess(true);
        toast("Admin access granted!");
        await fetchAllData();
      } else {
        toast("Incorrect password. Please try again.");
      }
    } catch (error) {
      console.error("Error signing in:", error);
      toast("An error occurred. Please try again.");
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('adminAccess');
    setHasAccess(false);
    setPassword("");
    toast("Signed out successfully");
  };

  const handleAcceptClaim = async (petId, claimId, claim) => {
    try {
      const pet = avPets && Object.values(avPets).find((pet) => pet.id === petId);

      if (!pet && !petId.startsWith('missing-')) {
        console.error("Pet not found for petId:", petId);
        toast("Pet not found");
        return;
      }

      const petSlug = pet?.slug || claim.slug;
      const pledges = requiresCapturePledges.filter(pledge => pledge.slug === claim.slug);

      if (pledges.length === 0) {
        toast("No pledges to process");
        return;
      }

      // Use new processConnectPayment function
      const processConnectPayment = httpsCallable(functions, "processConnectPayment");
      const paymentResult = await processConnectPayment({
        pledges: pledges,
        rescueStripeAccountId: claim.claimedStripe,
        dogSlug: petSlug,
        claimId: claimId,
        saviorReward: pet?.saviorReward || false,
        saviorUid: pet?.createdBy,
      });

      if (!paymentResult?.data || !paymentResult.data.success) {
        throw new Error(
          paymentResult.data?.error || "Failed to process payments"
        );
      }

      const { successCount, failCount, results } = paymentResult.data;

      if (failCount > 0) {
        console.warn(`${failCount} pledges failed to process:`, results.filter(r => !r.success));
        toast(`Processed ${successCount} pledges, ${failCount} failed`);
      } else {
        toast(`Successfully processed ${successCount} pledges`);
      }

      // Update local state
      setAvPets((prevPets) => ({
        ...prevPets,
        [petId]: {
          ...prevPets[petId],
          claimStatus: "accepted",
          euthanasiaStatus: "rescued",
        },
      }));

      // Refresh data
      await fetchAllData();
    } catch (error) {
      console.error("Error accepting claim:", error);
      toast("Failed to accept claim: " + error.message);
    }
  };

  const handleDenyClaim = async (petId, claimId) => {
    try {
      await firestore.collection("claims").doc(claimId).update({
        claimStatus: "denied",
      });
      toast("Claim denied");
      await fetchAllData();
    } catch (error) {
      console.error("Error denying claim:", error);
      toast("Failed to deny claim. Please try again.");
    }
  };

  const calculateTotalPledged = (pledges) => {
    if (!pledges || !Array.isArray(pledges)) return 0;
    return pledges.reduce((total, pledge) => {
      if (pledge.status === "requires_capture") {
        return total + (pledge.displayAmount || 0);
      }
      return total;
    }, 0);
  };

  const calculateTotalSubscriptions = (subscriptions) => {
    if (!subscriptions || !Array.isArray(subscriptions)) return 0;
    return subscriptions.reduce((total, sub) => total + (sub.amount || 0), 0);
  };

  if (!hasAccess) {
    return (
      <Container>
        <Title>PledgeFast Admin</Title>
        <InputContainer>
          <Input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSignIn()}
          />
          <LoginButton onClick={handleSignIn}>ACCESS ADMIN</LoginButton>
        </InputContainer>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100vh", color: "#FFFFFF", padding: "24px" }}>
      <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ color: "#FFFFFF", fontWeight: "600" }}>
            Admin Dashboard
          </Typography>
          <SignOutButton onClick={handleSignOut}>
            Sign Out
          </SignOutButton>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mb: 2, color: "#FFFFFF", fontWeight: "500" }}>
          Pending Claims ({unprocessedClaims.length})
        </Typography>

        {unprocessedClaims && unprocessedClaims.length > 0 ? (
          <Grid container spacing={2}>
            {unprocessedClaims.map((data, index) => {
              const pet = data.petProfile;
              const claim = data.claim;
              const pledges = data.pledges;
              const subscriptions = data.subscriptions || [];

              const petDisplayName = pet?.name || `Unknown Pet (${claim.slug})`;
              const petId = pet?.id || `missing-${claim.slug}`;
              const hoursAgo = Math.floor(
                (now - (claim.claimedAt?.toDate ? claim.claimedAt.toDate().getTime() : claim.claimedAt)) / 3600000
              );

              // Calculate live countdown
              let timeRemainingHHMMSS = null;
              if (pet?.euthanasiaDate) {
                const today = new Date(now).toISOString().split('T')[0];
                const isToday = pet.euthanasiaDate === today;

                let deadline = "T23:59:59";
                if (isToday && pet.shelter?.closingTime) {
                  const closeTime = pet.shelter.closingTime.toString().padStart(4, "0");
                  const hours = closeTime.slice(0, 2);
                  const minutes = closeTime.slice(2, 4);
                  deadline = `T${hours}:${minutes}:00`;
                }

                const euthanasiaDateTime = new Date(pet.euthanasiaDate + deadline);
                const difference = euthanasiaDateTime.getTime() - now;
                timeRemainingHHMMSS = formatCountdownTimer(difference);
              }

              return (
                <Grid item xs={12} key={petId}>
                  <Paper elevation={3} sx={{ p: 2, borderRadius: 2, backgroundColor: "#1F2937", border: "1px solid #374151" }}>
                    <Grid container spacing={2}>
                      {/* Left: Dog Info + Financials */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                          <Box>
                            <Typography
                              variant="h6"
                              component={pet ? Link : "div"}
                              to={pet ? `https://pledgefast.com/${pet.slug}` : undefined}
                              target={pet ? "_blank" : undefined}
                              rel={pet ? "noopener noreferrer" : undefined}
                              sx={{
                                color: pet ? "#FFFFFF" : "#EF4444",
                                textDecoration: "none",
                                fontWeight: "600",
                                mb: 0.5,
                                "&:hover": { color: pet ? "#10B981" : "#EF4444" },
                              }}
                            >
                              {petDisplayName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                              #{pet?.animalId || claim.animalId}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                            {timeRemainingHHMMSS && (
                              <Typography sx={{ fontSize: "11px", color: "#EF4444", fontWeight: "600" }}>
                                ⏰ {timeRemainingHHMMSS}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: "11px", color: "#9CA3AF" }}>
                              Claimed {hoursAgo}h ago
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#0F172A", borderRadius: "8px" }}>
                          <Typography sx={{ fontSize: "13px", color: "#10B981", fontWeight: "600", mb: 1 }}>
                            💰 Financials
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography sx={{ fontSize: "12px", color: "#D1D5DB" }}>
                              Pledges: ${calculateTotalPledged(pledges).toLocaleString()}
                            </Typography>
                            {pledges && pledges.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPledges(prev => ({ ...prev, [claim.id]: !prev[claim.id] }))}
                                sx={{ color: "#9CA3AF", padding: "2px" }}
                              >
                                {expandedPledges[claim.id] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </Box>
                          <Collapse in={expandedPledges[claim.id]}>
                            <Box sx={{ pl: 2, mt: 1, borderLeft: "2px solid #374151" }}>
                              {pledges && pledges.map((pledge, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: "11px", color: "#FFFFFF", fontWeight: "500" }}>
                                    ${pledge.displayAmount || pledge.totalAmount || 0}
                                    {pledge.userName && ` • ${pledge.userName}`}
                                  </Typography>
                                  <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>
                                    {pledge.createdAt ? new Date(pledge.createdAt).toLocaleString() : "N/A"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "12px", color: "#D1D5DB" }}>
                              Subscriptions: ${calculateTotalSubscriptions(subscriptions).toLocaleString()}/mo
                            </Typography>
                            {subscriptions && subscriptions.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPledges(prev => ({ ...prev, [`${claim.id}-subs`]: !prev[`${claim.id}-subs`] }))}
                                sx={{ color: "#9CA3AF", padding: "2px" }}
                              >
                                {expandedPledges[`${claim.id}-subs`] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </Box>
                          <Collapse in={expandedPledges[`${claim.id}-subs`]}>
                            <Box sx={{ pl: 2, mt: 1, borderLeft: "2px solid #374151" }}>
                              {subscriptions && subscriptions.map((sub, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: "11px", color: "#FFFFFF", fontWeight: "500" }}>
                                    ${sub.amount || 0}/mo
                                    {sub.userName && ` • ${sub.userName}`}
                                  </Typography>
                                  <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>
                                    {sub.createdAt?.toDate ? sub.createdAt.toDate().toLocaleString() : "N/A"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>

                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#0F172A", borderRadius: "8px", display: "flex", gap: 1.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "13px", color: "#F59E0B", fontWeight: "600", mb: 1 }}>
                              🏥 Rescue Organization
                            </Typography>
                            <Typography sx={{ fontSize: "13px", color: "#FFFFFF", fontWeight: "600", mb: 0.5 }}>
                              {claim.rescueOrgData?.organizationName || "Unknown Organization"}
                            </Typography>
                            {claim.rescueOrgData?.email && (
                              <Typography sx={{ fontSize: "11px", color: "#D1D5DB", mb: 0.5 }}>
                                📧 <a href={`mailto:${claim.rescueOrgData.email}`} style={{ color: "#10B981", textDecoration: "none" }}>
                                  {claim.rescueOrgData.email}
                                </a>
                              </Typography>
                            )}
                            {claim.rescueOrgData?.phoneNumber && (
                              <Typography sx={{ fontSize: "11px", color: "#D1D5DB", mb: 0.5 }}>
                                📞 <a href={`tel:${claim.rescueOrgData.phoneNumber}`} style={{ color: "#10B981", textDecoration: "none" }}>
                                  {claim.rescueOrgData.phoneNumber}
                                </a>
                              </Typography>
                            )}
                            {claim.rescueOrgData?.address && (
                              <Typography sx={{ fontSize: "11px", color: "#D1D5DB", mb: 0.5 }}>
                                📍 {claim.rescueOrgData.address}
                              </Typography>
                            )}
                            {claim.rescueOrgData?.website && (
                              <Typography sx={{ fontSize: "11px", color: "#D1D5DB", mb: 0.5 }}>
                                🌐 <a href={claim.rescueOrgData.website} target="_blank" rel="noopener noreferrer" style={{ color: "#10B981", textDecoration: "none" }}>
                                  {claim.rescueOrgData.website}
                                </a>
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            {(pet?.images?.[0] || pet?.videos?.[0]) ? (
                              pet?.videos?.[0] ? (
                                <video
                                  src={pet.videos[0]}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #374151"
                                  }}
                                />
                              ) : (
                                <img
                                  src={pet.images[0]}
                                  alt={`${petDisplayName} profile`}
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #374151"
                                  }}
                                />
                              )
                            ) : (
                              <Box sx={{
                                width: "120px",
                                height: "120px",
                                backgroundColor: "#1F2937",
                                borderRadius: "8px",
                                border: "1px solid #374151",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}>
                                <Typography sx={{ color: "#6B7280", fontSize: "10px", textAlign: "center" }}>
                                  No image
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            onClick={() => {
                              if (window.confirm("Are you sure you want to accept this claim?")) {
                                handleAcceptClaim(petId, claim.id, claim);
                              }
                            }}
                            sx={{ textTransform: "none", fontWeight: "600" }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={() => {
                              if (window.confirm("Are you sure you want to deny this claim?")) {
                                handleDenyClaim(petId, claim.id);
                              }
                            }}
                            sx={{ textTransform: "none", fontWeight: "600" }}
                          >
                            Deny
                          </Button>
                        </Box>
                      </Grid>

                      {/* Right: Proof Image */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ color: "#FFFFFF", fontWeight: "600", mb: 1 }}>
                          Proof of Rescue
                        </Typography>
                        <img
                          src={claim.proofOfRescueUrl}
                          alt="Proof of Rescue"
                          style={{
                            width: "100%",
                            maxHeight: "400px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #374151"
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: "#1F2937", border: "1px solid #374151" }}>
            <Typography variant="h6" align="center" sx={{ color: "#FFFFFF" }}>
              No pending claims
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  background-color: #000000;
  color: #FFFFFF;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 32px;
  color: #FFFFFF;
  text-align: center;
`;

const InputContainer = styled.div`
  margin-bottom: 24px;
  width: 100%;
  max-width: 400px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #1E293B;
  border: 1px solid #334155;
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 14px;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #10B981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }
`;

const LoginButton = styled(Button)`
  background-color: #10B981;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  text-transform: none;
  width: 100%;

  &:hover {
    background-color: #059669;
    transform: translateY(-1px);
  }
`;

const SignOutButton = styled(Button)`
  background-color: #DC2626;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;

  &:hover {
    background-color: #B91C1C;
  }
`;

export default AdminPage;
